import { useEffect } from 'react';

import { Table } from '@tanstack/react-table';

import { useDeepCompare, usePrevious } from '@hsp/react-hooks';
import { isDefined } from '@hsp/utilities';

import { ISort } from '@t/api/common';

import { useTableStoreSelector, useTableStoreSet } from 'components/table/hooks/context';
import { usePageChanged } from 'components/table/hooks/page-changed';
import { useGetSortItems } from 'components/table/hooks/sort-items';
import { IPageEvent } from 'types/components/table/page.event';

export const useOnChange = <TData>(
  table: Table<TData>,
  total: number,
  sort: string | undefined,
  onPage?: (e: IPageEvent) => void,
  sorts?: ISort<TData>[]
) => {
  const globalFilter = useTableStoreSelector((state) => state.globalFilter);
  const numberOfPages = useTableStoreSelector((state) => state.numberOfPages);
  const page = useTableStoreSelector((state) => state.page);
  const setStore = useTableStoreSet();

  const e = usePageChanged();
  const previousGlobalFilter = usePrevious(globalFilter);
  const previousPage = usePrevious(page);

  const sorting = useGetSortItems(sort ?? sorts);

  useDeepCompare(() => {
    setStore({ sorting });
  }, [setStore, sorting]);

  useDeepCompare(() => {
    const { getCanNextPage, getCanPreviousPage } = table;

    if (page !== previousPage) {
      setStore({ hasNextPage: getCanNextPage(), hasPreviousPage: getCanPreviousPage() });
    }
  }, [page, previousPage, setStore, table]);

  useEffect(() => {
    if (isDefined(e) && e.total > 0) {
      onPage?.(e);
    }
  }, [e, onPage]);

  useEffect(() => {
    if (isDefined(previousGlobalFilter) && globalFilter !== previousGlobalFilter) {
      table.setGlobalFilter(globalFilter);
    }
  }, [globalFilter, previousGlobalFilter, table]);

  useEffect(() => {
    setStore({
      numberOfPages: table.getPageCount(),
      total,
    });
  }, [setStore, table, total]);

  useEffect(() => {
    const { getCanNextPage, getCanPreviousPage } = table;

    setStore({ hasNextPage: getCanNextPage(), hasPreviousPage: getCanPreviousPage() });
  }, [numberOfPages, setStore, table]);
};
